// ----------------------------------------------------------
// © Copyright 2021 European Space Agency, 2021
// This file is subject to the terms and conditions defined in file 'LICENSE.txt', which is part of this [source code/executable] package. No part of the package, including this file, may be copied, modified, propagated, or distributed except according to the terms contained in the file ‘LICENSE.txt’.
// -----------------------------------------------------------
import dayjs from "dayjs";
import { isDefined, isUndefined } from "../../helpers/check";
import { formatLogs } from "../../helpers/format";
import { bReducer, datalabToInstanceProperty, flatten, mapBuildsToObject } from "../../helpers/utils";
import { getServiceContainer } from "../../service-container";
import { appState, datalabsState } from "../../store";
import { ResourceType } from "../../types/state-types";
import { Logger } from "../logger";
import { storageManager } from "../storage-manager";
const logger = new Logger('service', 'datalab');
const DATALABS_STORAGE_KEY = "datalabs";
const DATALAB_BASE_STORAGE_KEY = "datalab";
export class DatalabService {
    constructor() {
        this.apiService = getServiceContainer().resolve('IDLDApiService');
        this.state = getServiceContainer().resolve('IState');
    }
    /**
     * Update the metadata of a given datalab, in case of success it updates the store
     * @param id
     * @param metadata
     */
    async updateMetadata(id, metadata) {
        // ensure we are not sending more than expected
        metadata = metadata.map((metaD) => ({ value: metaD.value, name: metaD.name }));
        const { setDatalabMetadataEntries: datalab } = await this.apiService.setDatalabMetadataEntries(id, metadata);
        if (isDefined(datalab)) {
            // invalid the cache only if the data were saved properly
            this.invalid(datalab.id);
            // store in the appStore
            // we are recieving only the id and not the builds so better to refresh the datalab
            // later this can be improved by changing the setDatalabMetadataEntries to return the requivalent of getDatalab
            appState.datalab = await this.getDatalab(datalab.id);
        }
        return datalab;
    }
    /**
     * This method will fetch the datalab list from the store for a fast access and then
     * if not available it will fetch from the server and store it
     * if the data are too old
     * @returns a list of datalab public, private, shared
     */
    async getDatalabs() {
        var _a;
        logger.debug(`Getting All datalabs`);
        if (this._hasValidData("all")) {
            return datalabsState.all;
        }
        // check the localstorage
        const storedDatalabs = this.fetchFromStorage(DATALABS_STORAGE_KEY);
        if (isDefined(storedDatalabs)) {
            return (datalabsState.all = storedDatalabs);
        }
        const clusterId = (_a = this.state.cluster) === null || _a === void 0 ? void 0 : _a.id;
        if (isUndefined(clusterId)) {
            return [];
        }
        const allLabsAndBuilds = await this.apiService.getDatalabs(clusterId);
        if (isUndefined(allLabsAndBuilds) || isUndefined(allLabsAndBuilds.datalabs)) {
            return [];
        }
        const [datalabs, bookmarked, running] = this._datalabs_post_processing(allLabsAndBuilds);
        //save the data in the stores
        // store the running datalab into the edisoft datalab state
        this.state.datalabs = allLabsAndBuilds.runningInstances;
        // set the datalab list in the store
        // doing it at the end will trigger a single  refresh
        datalabsState.bookmarked = bookmarked;
        datalabsState.running = running;
        datalabsState.all = datalabs;
        storageManager.store(datalabs, DATALABS_STORAGE_KEY, true);
        return datalabs;
    }
    /**
     * Enrich the data model based on the multiple element of the query
     * @param allLabsAndBuilds
     * @returns
     */
    _datalabs_post_processing(allLabsAndBuilds) {
        // flatten the bookmark,running and build for easy search
        const bookmarkedBuilds = allLabsAndBuilds.bookmarkedBuilds ? allLabsAndBuilds.bookmarkedBuilds.map((bookmarkedBuild) => bookmarkedBuild.datalab) : [];
        const runningInstances = allLabsAndBuilds.runningInstances ? allLabsAndBuilds.runningInstances.map(datalabToInstanceProperty(allLabsAndBuilds.builds)) : [];
        const datalabBuilds = mapBuildsToObject(allLabsAndBuilds.builds, "datalabId");
        // storage for the different state
        const bookmarked = [];
        const indexedDatalab = {};
        // augment the datalab with its builds
        const datalabs = allLabsAndBuilds.datalabs.map((datalab) => {
            // this code could become the bottleneck part of the application if we are recieving
            // tons of datalab
            // set the list of builds,running state and bookmark flag
            datalab = Object.assign(Object.assign({}, datalab), { builds: isDefined(datalabBuilds[datalab.id]) ? datalabBuilds[datalab.id] : [], bookmarked: bookmarkedBuilds.indexOf(datalab.id) > -1 });
            if (datalab.bookmarked) {
                bookmarked.push(datalab);
            }
            indexedDatalab[datalab.id] = datalab;
            return datalab;
        });
        // for each running instance we create a datalab with a running status
        const running = runningInstances.map((runningInstance) => {
            return Object.assign({ runningStatus: runningInstance }, indexedDatalab[runningInstance.datalab]);
        });
        return [datalabs, bookmarked, running];
    }
    /**
     * Retrieve a single datalab with its builds, it will get the information
     * from the localstorage and the cache before fetching from the server
     * @param datalabId
     * @returns
     */
    async getDatalab(datalabId) {
        logger.debug(`Getting ${datalabId}`);
        const storageKey = `${DATALAB_BASE_STORAGE_KEY}.${datalabId}`;
        const storedDatalab = this.fetchFromStorage(storageKey);
        if (isDefined(storedDatalab)) {
            return storedDatalab;
        }
        const { lab, builds } = await this.apiService.getDatalab(datalabId);
        // check that we get something, either the datalab doesn't exist or the datalab is not accessible
        if (isUndefined(lab)) {
            return null;
        }
        // merge the lab with its builds
        const datalab = Object.assign(Object.assign({}, lab), { builds: builds.map(build => formatLogs(build)) });
        // Store the datalab for another call, this will require to invalidate the storage at each change of the datalab
        if (isDefined(datalab)) {
            storageManager.store(datalab, storageKey);
        }
        return datalab;
    }
    /**
     * Search the datalab by query
     * @param qs
     */
    async searchDatalab(qs) {
        var _a;
        logger.debug(`Searching datalabs '${qs}'`);
        const storageKey = `${DATALABS_STORAGE_KEY}.${storageManager.utf8ToB64(qs)}`;
        // check the localstorage
        const storedDatalabs = this.fetchFromStorage(storageKey);
        if (isDefined(storedDatalabs)) {
            return storedDatalabs;
        }
        const clusterId = (_a = this.state.cluster) === null || _a === void 0 ? void 0 : _a.id;
        const allLabsAndBuilds = await this.apiService.searchContent(qs, clusterId);
        const [datalabs, ,] = this._datalabs_post_processing(allLabsAndBuilds);
        // Store the datalab for another call, this will require to invalidate the storage at each change of the datalab
        if (isDefined(datalabs)) {
            storageManager.store(datalabs, storageKey);
        }
        return datalabs;
    }
    /**
     * Bookmark a datalab, as we can only bookmark the build of a datalab, this method
     * is bookmarking always the latest build
     * @param datalab
     */
    async bookmark(datalab) {
        logger.debug(`Bookmark datalab '${datalab.id}'`);
        if (datalab.builds.length == 0) {
            throw "You can't bookmark a datalab without any build!";
        }
        // for now the database and data model is only bookmarking builds which doesn't have anymore sense
        const done = await this.apiService.setDatalabBookmark(datalab.id, true);
        if (!!done) {
            // mark it so the UI can be adapted
            datalab.bookmarked = true;
            // add it to the section
            datalabsState.bookmarked = [...datalabsState.bookmarked, datalab];
            // inform the rest of the application that we have a new bookmarked datalab
            appState.bookmarked = datalab;
        }
        return done;
    }
    /**
     * Remove the bookmark on all the builds associated to the datalab
     * @param datalab
     */
    async unBookmark(datalab) {
        logger.debug(`Unbookmark datalab '${datalab.id}'`);
        const done = await this.apiService.setDatalabBookmark(datalab.id, false);
        if (!!done) {
            datalab.bookmarked = false;
            // remove from the state
            datalabsState.bookmarked = datalabsState.bookmarked.filter((bmDatalab) => bmDatalab.id !== datalab.id);
            // inform the rest of the application that we have a datalab not bookmarked anymore
            appState.unBookmarked = datalab;
        }
        return done;
    }
    /**
     * Share the datalab with the list of users
     * @param datalab
     * @param users
     * @returns
     */
    async shareDatalab(datalab, users) {
        const flatSelectedUsers = flatten(users, "username");
        // retrieve datalabe shared access
        const accessList = await this.apiService.getDatalabAccessList(datalab.id);
        const flatCurrentUsers = flatten(accessList, "username");
        // compare the list with the actual to get the removed ones
        const removedUsers = accessList.filter(user => flatSelectedUsers.indexOf(`&${user.username}&`) == -1);
        if (removedUsers.length > 0) {
            // revoke the missing
            let revoked = [false];
            try {
                revoked = await Promise.all(removedUsers.map(async (user) => await this.apiService.revokeAccess(datalab.id, user.username)));
            }
            catch (e) {
                logger.error(`Failed to revoke access for ${e}`);
            }
            if (!revoked.reduce(bReducer, true)) {
                // something went wrong
                return false;
            }
        }
        // compare the list with the actual to get the added ones
        const addedUsers = users.filter(user => flatCurrentUsers.indexOf(`&${user.username}&`) == -1);
        if (addedUsers.length > 0) {
            // grant the new
            let added = [];
            try {
                added = await Promise.all(addedUsers.map(async (user) => await this.apiService.grantAccess(datalab.id, user.username)));
            }
            catch (e) {
                logger.error(`Failed to grant access for ${e}`);
            }
            if (!added.reduce(bReducer, true)) {
                return false;
            }
        }
        return true;
    }
    /**
     * Archive a build of a datalab
     * @param datalab
     * @param build
     * @returns
     */
    async archive(datalab, build) {
        // check the current status of the build
        let archived = false;
        if (build.status == "PUBLISHED" /* Status.PUBLISHED */) {
            const buildId = await this.apiService.archiveDatalabBuild(build.id);
            // if we get something in return it means we have succeeded
            archived = isDefined(buildId);
            //invalid the cache as the state of the datalab has changed
            this.refreshDatalab(datalab);
        }
        return archived;
    }
    /**
     * Archive a build of a datalab
     * @param datalab
     * @param build
     * @returns
     */
    async unArchive(datalab, build) {
        // check the current status of the build
        let archived = false;
        if (build.status == "ARCHIVED" /* Status.ARCHIVED */) {
            const buildId = await this.apiService.unarchiveBuildDatalabBuild(build.id);
            // if we get something in return it means we have succeeded
            archived = isDefined(buildId);
            //invalid the cache as the state of the datalab has changed
            this.refreshDatalab(datalab);
        }
        return archived;
    }
    /**
     * Wrapper arroung the storage to check the state of the cache
     * @param storageKey
     * @returns
     */
    fetchFromStorage(storageKey) {
        if (!storageManager.CACHE_ENABLED) {
            return null;
        }
        return storageManager.retrieve(storageKey);
    }
    /**
     * Check the data validity
     * @param dataHolder
     * @returns
     */
    _hasValidData(dataHolder) {
        // this will allow to deactivate the cache for the entire application in one place
        if (!storageManager.CACHE_ENABLED) {
            return false;
        }
        logger.debug(datalabsState);
        if (isUndefined(datalabsState.lastUpdate[dataHolder])) {
            logger.debug("No timestamp");
            // no last modified data means no data so we need to fetch it
            return false;
        }
        logger.debug(dayjs().diff(dayjs(datalabsState.lastUpdate[dataHolder])));
        // ttl: 1 minute aka 1000*60
        if (dayjs().diff(dayjs(datalabsState.lastUpdate[dataHolder])) > 1000 * 60) {
            logger.debug("Too old");
            // data are too old
            return false;
        }
        logger.info("Use cached data");
        return true;
    }
    /**
     * Remove a datalab from the database
     * @param datalabId
     */
    async removeDatalab(datalabId) {
        logger.info(`Removing datalab ${datalabId}`);
        const response = await this.apiService.removeDatalab(datalabId);
        if (response) {
            // clean the storage from the datalab
            this.invalid(datalabId);
            // clean the storage of the list to be refreshed
            this.invalid();
            // fetching the new datalab list
            this.getDatalabs();
        }
        return response;
    }
    /**
     * Remove from the storage and the cache the datalab or the list of dalalab depending on the parameter
     * @param datalabId Optional value, if not passed the entire list of datalab will be removed
     */
    invalid(datalabId) {
        logger.info(`invalidating ${isDefined(datalabId) ? datalabId : "All datalab"}`);
        const storageKey = isDefined(datalabId) ? `${DATALAB_BASE_STORAGE_KEY}.${datalabId}` : DATALABS_STORAGE_KEY;
        storageManager.remove(storageKey);
        // removing the last update date will trigger a refresh
        datalabsState.lastUpdate = {};
    }
    async refreshDatalab(datalab) {
        // need to update the status of the datalab
        // invalid the cache
        this.invalid(datalab.id);
        // fetch a new version of the datalab
        // is it wise to update the store here or better to do that in the datalabservice so it is done automatically
        // what's make sure getting a datalab is expecting to have it active, risk of side effect
        appState.datalab = await this.getDatalab(datalab.id);
        // invalid all the datalabs to get a fresh list
        // could be optimized by having a different cache and store for public and private datalabs
        this.invalid();
        // fetch the whole list in background so the user won't have the impression of calls when back to the list
        await this.getDatalabs();
    }
    getDatalabInstanceViewer(datalab) {
        var _a;
        if (((_a = datalab.runningStatus.resources) === null || _a === void 0 ? void 0 : _a.length) > 0) {
            // search for the interface
            const interfaces = datalab.runningStatus.resources.filter((resource) => resource.type == ResourceType.Interface);
            return `/datalab-viewer/${interfaces[0].id}`;
        }
        else {
            // we don't have yet the resources for the datalab so we redirect to the waiting page
            return `/wait/${datalab.id}`;
        }
    }
}
